import { useCallback, useEffect, useMemo, useState } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { t } from 'i18next';
const ERROR_MESSAGES = {
  // FetchBaseQueryError коды (number)
  400: t('errors.fetchBaseQueryError.400'),
  401: t('errors.fetchBaseQueryError.401'),
  403: t('errors.fetchBaseQueryError.403'),
  404: t('errors.fetchBaseQueryError.404'),
  409: t('errors.fetchBaseQueryError.409'),
  429: t('errors.fetchBaseQueryError.429'),
  500: t('errors.fetchBaseQueryError.500'),
  502: t('errors.fetchBaseQueryError.502'),
  503: t('errors.fetchBaseQueryError.503'),

  // FetchBaseQueryError коды (string)
  FETCH_ERROR: t('errors.fetchBaseQueryError.FETCH_ERROR'),
  PARSING_ERROR: t('errors.fetchBaseQueryError.PARSING_ERROR'),
  TIMEOUT_ERROR: t('errors.fetchBaseQueryError.TIMEOUT_ERROR'),
  CUSTOM_ERROR: t('errors.fetchBaseQueryError.CUSTOM_ERROR'),

  // SerializedError коды
  ECONNABORTED: t('errors.serializedError.ECONNABORTED'),
  ENOTFOUND: t('errors.serializedError.ENOTFOUND'),
  ETIMEDOUT: t('errors.serializedError.ETIMEDOUT'),
  UNKNOWN: t('errors.serializedError.UNKNOWN'),
};

const isFetchBaseQueryError = (
  error: FetchBaseQueryError | SerializedError | undefined,
): error is FetchBaseQueryError => {
  return (
    error !== undefined &&
    typeof error === 'object' &&
    ('status' in error || 'data' in error)
  );
};

const isSerializedError = (
  error: FetchBaseQueryError | SerializedError | undefined,
): error is SerializedError => {
  return (
    error !== undefined &&
    typeof error === 'object' &&
    ('code' in error ||
      'name' in error ||
      'stack' in error ||
      'message' in error)
  );
};

export const useQueryError = (
  isError: boolean,
  error?: FetchBaseQueryError | SerializedError,
) => {
  const [showError, setShowError] = useState(false);

  const errorMessage = useMemo(() => {
    if (!error) return ERROR_MESSAGES.UNKNOWN;

    if (isFetchBaseQueryError(error)) {
      const data = error.data as { error: string } | undefined;
      return (
        data?.error ||
        ERROR_MESSAGES[error.status as keyof typeof ERROR_MESSAGES] ||
        ERROR_MESSAGES.UNKNOWN
      );
    }

    if (isSerializedError(error)) {
      return (
        ERROR_MESSAGES[error.code as keyof typeof ERROR_MESSAGES] ||
        ERROR_MESSAGES.UNKNOWN
      );
    }

    return ERROR_MESSAGES.UNKNOWN;
  }, [error]);

  const closeError = useCallback(() => {
    setShowError(false);
  }, []);

  useEffect(() => {
    if (!isError || !error) {
      setShowError(false);
      return;
    }

    setShowError(true);
  }, [isError, error]);

  return useMemo(
    () => ({ showError, errorMessage, closeError }),
    [showError, errorMessage, closeError],
  );
};
